/**
 * Tracks in-progress Airbyte connector setups in sessionStorage so that polling
 * survives navigation between the connectors page and the Airbyte config form.
 *
 * Consumers drive RTK Query's pollingInterval from `useAirbytePollingInterval()`
 * (see `~/ui-rtk/hooks/airbyte-polling`). RTK Query dedupes across all
 * subscribers of the same query, so polling happens at most at
 * `AIRBYTE_POLLING_INTERVAL_MS` even when many components subscribe.
 */

export const AIRBYTE_POLLING_PREFIX = "airbyte-polling:"
export const AIRBYTE_POLLING_START_TIME_SUFFIX = ":start-time"
export const AIRBYTE_POLLING_INTERVAL_MS = 5_000
export const AIRBYTE_MAX_POLLING_DURATION_MS = 3 * 60 * 1_000

type Listener = () => void

const listeners = new Set<Listener>()

function notify() {
  for (const listener of listeners) listener()
}

function isBrowser() {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined"
}

function startTimeKey(connectorId: string) {
  return `${AIRBYTE_POLLING_PREFIX}${connectorId}${AIRBYTE_POLLING_START_TIME_SUFFIX}`
}

function pollingKey(connectorId: string) {
  return `${AIRBYTE_POLLING_PREFIX}${connectorId}`
}

export function getActiveAirbytePollingKeys(): string[] {
  if (!isBrowser()) return []
  return Object.keys(sessionStorage).filter(
    key =>
      key.startsWith(AIRBYTE_POLLING_PREFIX) &&
      !key.endsWith(AIRBYTE_POLLING_START_TIME_SUFFIX),
  )
}

export function hasActiveAirbytePolling(): boolean {
  return getActiveAirbytePollingKeys().length > 0
}

export function startAirbytePolling(
  connectorId: string,
  now: number = Date.now(),
): void {
  if (!isBrowser()) return
  sessionStorage.setItem(pollingKey(connectorId), "true")
  sessionStorage.setItem(startTimeKey(connectorId), now.toString())
  notify()
}

export function stopAirbytePolling(connectorId: string): void {
  if (!isBrowser()) return
  const hadKey = sessionStorage.getItem(pollingKey(connectorId)) !== null
  sessionStorage.removeItem(pollingKey(connectorId))
  sessionStorage.removeItem(startTimeKey(connectorId))
  if (hadKey) notify()
}

/**
 * Removes polling entries older than AIRBYTE_MAX_POLLING_DURATION_MS.
 * Call from a periodic timer while polling is active.
 */
export function expireStaleAirbytePolling(now: number = Date.now()): void {
  if (!isBrowser()) return
  let changed = false
  for (const key of getActiveAirbytePollingKeys()) {
    const rawStart = sessionStorage.getItem(
      `${key}${AIRBYTE_POLLING_START_TIME_SUFFIX}`,
    )
    const start = rawStart ? parseInt(rawStart, 10) : 0
    if (!start) {
      // No start-time recorded — backfill so it eventually expires.
      sessionStorage.setItem(
        `${key}${AIRBYTE_POLLING_START_TIME_SUFFIX}`,
        now.toString(),
      )
      continue
    }
    if (now - start > AIRBYTE_MAX_POLLING_DURATION_MS) {
      sessionStorage.removeItem(key)
      sessionStorage.removeItem(`${key}${AIRBYTE_POLLING_START_TIME_SUFFIX}`)
      changed = true
    }
  }
  if (changed) notify()
}

/**
 * Removes polling entries whose connector is no longer known or has reached
 * a terminal status. Called by the polling manager when connector data updates.
 */
export function reconcileAirbytePolling(
  knownTerminalConnectorIds: ReadonlySet<string>,
  knownConnectorIds: ReadonlySet<string>,
): void {
  if (!isBrowser()) return
  let changed = false
  for (const key of getActiveAirbytePollingKeys()) {
    const connectorId = key.slice(AIRBYTE_POLLING_PREFIX.length)
    if (
      !knownConnectorIds.has(connectorId) ||
      knownTerminalConnectorIds.has(connectorId)
    ) {
      sessionStorage.removeItem(key)
      sessionStorage.removeItem(`${key}${AIRBYTE_POLLING_START_TIME_SUFFIX}`)
      changed = true
    }
  }
  if (changed) notify()
}

export function subscribeAirbytePolling(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
